import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import apiService from "../../services/api";
import { CreativeLookTemplate } from "../../types";
import CreativeLookForm from "./CreativeLookForm";
import { getImageUrlWithFallback } from "../../utils/imageUrlHelper";

const CreativeLooksList = () => {
  const [templates, setTemplates] = useState<CreativeLookTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CreativeLookTemplate | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "formal", label: "Formal" },
    { value: "casual", label: "Casual" },
    { value: "traditional", label: "Traditional" },
    { value: "fantasy", label: "Fantasy" },
    { value: "professional", label: "Professional" },
    { value: "seasonal", label: "Seasonal" },
    { value: "artistic", label: "Artistic" },
    { value: "historical", label: "Historical" },
    { value: "futuristic", label: "Futuristic" },
    { value: "other", label: "Other" },
  ];

  const statuses = [
    { value: "all", label: "All Statuses" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "draft", label: "Draft" },
  ];

  useEffect(() => {
    fetchTemplates();
  }, [filterCategory, filterStatus, page]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError("");

      const filters: any = {
        page,
        limit: 20,
      };

      if (filterCategory !== "all") {
        filters.category = filterCategory;
      }

      if (filterStatus !== "all") {
        filters.status = filterStatus;
      }

      if (searchTerm) {
        filters.search = searchTerm;
      }

      const response = await apiService.getAllCreativeLookTemplates(filters);

      if (response.success && response.data) {
        // Handle response structure: data is array, meta has total
        const templates = Array.isArray(response.data) ? response.data : [];
        const total = (response as any).meta?.total || 0;
        setTemplates(templates);
        setTotal(total);
      } else {
        setError(response.error || "Failed to load templates");
        setTemplates([]);
        setTotal(0);
      }
    } catch (err: any) {
      console.error("Error fetching templates:", err);
      setError(err.response?.data?.error || err.message || "Failed to load templates");
      setTemplates([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchTemplates();
  };

  const handleAdd = () => {
    setSelectedTemplate(null);
    setFormOpen(true);
  };

  const handleEdit = (template: CreativeLookTemplate) => {
    setSelectedTemplate(template);
    setFormOpen(true);
  };

  const handleDelete = (template: CreativeLookTemplate) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      const response = await apiService.deleteCreativeLookTemplate(selectedTemplate._id);

      if (response.success) {
        toast.success("Template deleted successfully");
        setDeleteDialogOpen(false);
        setSelectedTemplate(null);
        fetchTemplates();
      } else {
        toast.error(response.error || "Failed to delete template");
      }
    } catch (err: any) {
      console.error("Error deleting template:", err);
      toast.error(err.response?.data?.error || err.message || "Failed to delete template");
    }
  };

  const handleToggleStatus = async (template: CreativeLookTemplate) => {
    try {
      const newStatus = template.status === "active" ? "inactive" : "active";
      const response = await apiService.updateCreativeLookTemplate(template._id, {
        status: newStatus,
      });

      if (response.success) {
        toast.success(`Template ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
        fetchTemplates();
      } else {
        toast.error(response.error || "Failed to update status");
      }
    } catch (err: any) {
      console.error("Error updating status:", err);
      toast.error(err.response?.data?.error || err.message || "Failed to update status");
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedTemplate(null);
  };

  const handleFormSuccess = () => {
    fetchTemplates();
    handleFormClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "error";
      case "draft":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1">
          Creative Look Templates
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Template
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          sx={{ minWidth: 250 }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={handleSearch} edge="end">
                <SearchIcon />
              </IconButton>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Category</InputLabel>
          <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} label="Category">
            {categories.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
            {statuses.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Box sx={{ p: 3, bgcolor: "error.light", borderRadius: 1, mb: 3 }}>
          <Typography color="error.dark">{error}</Typography>
        </Box>
      )}

      {/* Templates Grid */}
      {!loading && !error && (
        <>
          <Grid container spacing={3}>
            {templates.map((template) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={template._id}>
                <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={getImageUrlWithFallback(template.referenceImageUrl)}
                    alt={template.name}
                    sx={{ objectFit: "cover", backgroundColor: "grey.200" }}
                    onError={(e: any) => {
                      e.target.src = "https://via.placeholder.com/400x300?text=No+Image";
                    }}
                  />

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" gutterBottom noWrap>
                      {template.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, height: 40, overflow: "hidden" }}>
                      {template.description || "No description"}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                      <Chip label={template.category} size="small" color="primary" variant="outlined" />
                      <Chip label={`${template.credits} credits`} size="small" variant="outlined" />
                      <Chip label={`${template.uses} uses`} size="small" />
                    </Box>

                    <Chip label={template.status} size="small" color={getStatusColor(template.status)} />
                  </CardContent>

                  <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(template)}
                        color={template.status === "active" ? "success" : "default"}
                        title={template.status === "active" ? "Deactivate" : "Activate"}
                      >
                        {template.status === "active" ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEdit(template)} color="primary" title="Edit">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(template)} color="error" title="Delete">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Order: {template.displayOrder}
                    </Typography>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {templates.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No templates found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {searchTerm || filterCategory !== "all" || filterStatus !== "all"
                  ? "Try adjusting your filters"
                  : "Click 'Add Template' to create your first template"}
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          {total > 20 && (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
              <Button variant="outlined" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Typography sx={{ display: "flex", alignItems: "center", px: 2 }}>
                Page {page} of {Math.ceil(total / 20)}
              </Typography>
              <Button variant="outlined" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Form Dialog */}
      {formOpen && <CreativeLookForm open={formOpen} onClose={handleFormClose} onSuccess={handleFormSuccess} template={selectedTemplate} />}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedTemplate?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreativeLooksList;
